import { useEffect, useState } from 'react';
import {
  fetchMissionGeneration,
  regenerateMission,
  type GenerationView,
  type MissionView,
} from '../api/missions';
import { ca } from '../i18n/ca';
import { useMeta } from './MetaContext';
import { useConfirm } from './useConfirm';
import { errorText } from './useSessionLive';

/**
 * A stored mission says when it is older than the generator's rules. A
 * mission keeps the definition it was generated with, and what was added
 * since (a helicopter given a job, new radio lines) does not reach it until
 * it is generated again: the owner's mission of 2026-10-11 went into a night
 * without them and nothing on its page said why. The line names the version
 * it was made with and carries the action that brings it up to date.
 *
 * Nothing is shown for a mission made with the current rules, whatever the
 * platform's version was then: the platform compares the generator's own
 * revision, which moves only when what it writes does.
 *
 * `generation` is what the mission's own view carries; a page that has only
 * the code (a test card about to start a server) leaves it undefined and the
 * notice asks for it.
 */
export function GenerationNotice({
  code,
  generation,
  onRegenerated,
  compact = false,
}: {
  code: string;
  /** From the mission's view; undefined to have it asked for by code. Null: nothing to say. */
  generation?: GenerationView | null;
  onRegenerated?: (mission: MissionView) => void;
  /** One paragraph and the button, for a place that already has a heading of its own. */
  compact?: boolean;
}) {
  const t = ca.script.stale;
  const { meta } = useMeta();
  const [asked, setAsked] = useState<GenerationView | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirm();

  useEffect(() => {
    if (generation !== undefined) return;
    let live = true;
    fetchMissionGeneration(code)
      .then((g) => {
        if (live) setAsked(g);
      })
      // An older platform, or a mission this account cannot read: nothing to say.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [code, generation]);

  const g = generation !== undefined ? generation : asked;
  if (done) {
    return (
      <p className="notice small" role="status">
        {t.done}
      </p>
    );
  }
  if (!g || !g.stale) return null;

  async function regenerate(base: number) {
    const yes = await confirm({ ...t.ask, confirmLabel: ca.script.regenerateConfirm });
    if (!yes) return;
    setBusy(true);
    setError(null);
    try {
      const mission = await regenerateMission(code, base, {});
      setDone(true);
      onRegenerated?.(mission);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const version = meta?.version && meta.version !== 'dev' ? meta.version : null;
  return (
    <div
      className={`notice warn${compact ? ' small' : ''}`}
      role="status"
      aria-label={t.title}
      data-testid="generation-notice"
    >
      {confirmDialog}
      <p>
        {t.made(g.platform)} {t.changed(version)}
      </p>
      {g.regenerable ? (
        <p className="row actions">
          <button type="button" disabled={busy} onClick={() => void regenerate(g.missionRevision)}>
            <i className="bi bi-arrow-repeat" aria-hidden="true" />{' '}
            {busy ? ca.script.regenerating : t.regenerate}
          </button>
        </p>
      ) : (
        <p className="muted small">{t.notYours}</p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
